// External dependencies
import moment from 'moment';

// Internal dependencies
import { generateScore } from './generateScore';
import { validateSelections, filterInvalidActivities, getSchoolYear } from '../validation';
import { shouldFilterStoryFromReport } from '../../../../services/EarlyReaderService';
import getArmScore from '../getArmScore';
import store from '../../../../store';
import { fetchScaledCutlines as fetchScaledCutlinesAPI } from '../../../../services/query/benchmarkReportAPI';
import { getSchoolYearWithFallbacks } from '../../../../utilities/getSchoolYearWithFallbacks';
import { getScaleFromMetricId } from '../metricScaleUtils';

const subScores = [
  { metric: 'armScoreAdjusted', chartName: 'Amira Reading Mastery (ARM)' },
  { metric: 'bkScore', chartName: 'Background Knowledge' },
  { metric: 'decodeScore', chartName: 'Decoding' },
  { metric: 'esriScore', chartName: 'High Frequency Words' },
  { metric: 'equatedWCPM', chartName: 'Oral Reading Fluency' },
  { metric: 'phonAwareScore', chartName: 'Phonological Awareness' },
  { metric: 'compScore', chartName: 'Structures & Reasoning' },
  { metric: 'vocabularyScreenerScore', chartName: 'Vocabulary' }
  //{ metric: 'ISIP_overall_universal', chartName: 'ISIP Overall Score' },
  //{ metric: 'lkScore', chartName: 'Letter Knowledge' },
  //{ metric: 'ranSpeed', chartName: 'RAN Speed' },
  //{ metric: 'spellingScore', chartName: 'Spelling' },
];

export async function generateProgressReport(activities, metricId, scaleId, locale, pageType = 'default', equating = true) {
 if (pageType === 'studentSummary') {
  let report = await generateProgressReportPerMetric(activities, metricId, scaleId, locale, pageType, equating);
  const subscoreReports = [];
  
  // Process each subscore
  for (const subscore of subScores) {

    let subscoreReport = await generateProgressReportPerMetric(activities, subscore.metric, subscore.metric, locale, pageType);
    
    // Only add to reports if there's a valid latestScore
    if (subscoreReport.latestScore) {
      subscoreReport.metric = subscore.metric;
      subscoreReport.chartName = subscore.chartName;
      subscoreReports.push(subscoreReport);
    }
  }
  
  report.subscores = subscoreReports;
  return report;

 } else {
  return await generateProgressReportPerMetric(activities, metricId, scaleId, locale, pageType);
 }
}

export async function generateProgressReportPerMetric(activities, metricId, scaleId, locale, pageType = 'default') {
  // search for "ARM SPANISH SCORES" to find other places where ARM score is determined.
  // Check for valid selections and activities
  // Skip validateSelections check for Student Summary Page
  if(!activities || activities.length === 0 || (pageType !== 'studentSummary' && !validateSelections(metricId, scaleId, {}))) {
    return {
      data: [],
      mostRecentScore: '',
    };
  }

  const activitiesCopy = JSON.parse(JSON.stringify(activities)); // create a deep copy of the activities

  // Filter down on the valid activities
  let filteredActivities = filterInvalidActivities(activitiesCopy, true, true, true);

  let data = filteredActivities.map(activity => {
    activity.scores.armScore = getArmScore(activity.student.id, activity, locale);

    const prMetricId = metricId === "ARM" || metricId === "armScoreAdjusted" ? "armScorePR" : metricId === "equatedWCPM" ? "equatedWCPMPR" : `${metricId}_PR`;

    let score = generateScore(activity, metricId, scaleId, true, locale);
    let scorePR = generateScore(activity, prMetricId, prMetricId, false, locale);
    
    const transformedActivity = {
      id: activity.activityId,
      timeRead: activity.sessionTime ? activity.sessionTime : '',
      storyTitle: activity.story && activity.story.title,
      storyGrade: activity.story && activity.story.grade,
      storyTags: activity.story && activity.story.tags,
      activityTags: activity.tags,
      score,
      scorePR,
      date: moment(activity.createdAt).valueOf(),
      type: activity.type,
      status: activity.status,
      displayStatus: activity.displayStatus,
      firstName: activity.student.first_name,
      hasComprehensionDialog: activity.comprehensionDialog && activity.comprehensionDialog.length
    };
    
    return transformedActivity;
  });

  // sort activites by date
  data = data.sort(function(a,b){
    let aDate = moment(a.date).isAfter(moment(b.date));
    if(aDate) return 1;
    return -1;
  });

  // Find the most recent score, ignoring early reader activities
  let latestScore, latestPRScore, lastDate = null;
  let latestActivity = null;
  for (const activity of data) {
    if (shouldFilterStoryFromReport(activity, scaleId)) continue;
    const date = new Date(activity.date)
    if (!lastDate || date > lastDate) {
      latestScore = activity.score;
      latestPRScore = activity.scorePR;
      lastDate = date;
      latestActivity = activity;
    }
  }

  let scaledCutlines;
  if (latestScore !== null && latestScore !== undefined) {
    const student = activities[0]?.student;
    if (student) {
      const state = store.getState();
      const districtId = state.reports?.roster?.data?.district?.[0]?.id;
      const schoolId = state.reports?.roster?.data?.school?.[0]?.id;
      const grade = state.reports?.roster?.ui?.selections?.student?.grade;
      
      if (districtId && schoolId && grade) {
        try {
          const assessmentCalendar = state.calendar.assessmentCalendar;
          const scale = getScaleFromMetricId(metricId);
          if (assessmentCalendar?.periods?.length) {
            const periodIds = assessmentCalendar.periods.map(period => period.periodIdentifier);
            const params = {
              districtId,
              schoolId,
              grade: grade,
              scale,
              periodId: periodIds
            };
            scaledCutlines = await fetchScaledCutlinesAPI(params);
          } else {
            console.warn('No assessment calendar periods available');
          }
        } catch (error) {
          console.error(`❌ Error fetching cutlines for ${metricId}:`, error);
        }
      } else {
        console.warn('Missing required data for fetching cutlines for metric:', metricId, { districtId, schoolId, grade });
      }
    }
  }

  let yearStartDate, yearEndDate;
  let cutlines;
  let cutlineSeries = [];
  let numOfCutlines;

  const defaultReturn = {
    data,
    latestScore,
    cutlineSeries: [],
    poorCutoff: [],
    greatCutoff: [],
    onTrackCutoff: [],
    yearStart: moment(yearStartDate).valueOf(),
    yearEnd: moment(yearEndDate).valueOf(),
    yMax: 0,
    yMin: 0,
    label: scaleId,
    scale: scaleId,
    trendline: [],
  };

  if (!scaledCutlines) return defaultReturn;
  const periodIds = [...new Set(scaledCutlines.map(cutline => cutline.periodId))];
  // Check if we have valid periodIds
  if (!periodIds || periodIds.length === 0) {
    return defaultReturn;
  }

  numOfCutlines = scaledCutlines.length / periodIds.length;

  // Calculate the chart area height for each cutline
  const valuesMap = {};
  for (let i = 0; i < scaledCutlines.length; i++) {
    if (i % numOfCutlines === 0) {
      // use raw value for the first cutline
      valuesMap[scaledCutlines[i].value] = scaledCutlines[i].value;
    } else {
      // calculate the difference between the current and previous values to get the height of chart area
      valuesMap[scaledCutlines[i].value] = scaledCutlines[i].value - scaledCutlines[i-1].value;
    }
  }

  const state = store.getState();
  const assessmentCalendar = state.calendar?.assessmentCalendar;
  const schoolYearCalendar = state.calendar?.schoolYearCalendar;
  
  // Get school year dates using fallback utility
  const schoolYearResult = getSchoolYearWithFallbacks(assessmentCalendar, schoolYearCalendar);
  yearStartDate = moment(schoolYearResult.startDate).toISOString();
  yearEndDate = moment(schoolYearResult.endDate).toISOString();

  const processedCutlines = scaledCutlines.map((cutline, index) => {
    const periodId = cutline.periodId;
    const periodStartDate = moment(assessmentCalendar.periods[periodId].startDate);
    const periodEndDate = moment(assessmentCalendar.periods[periodId].endDate);
    const periodDuration = periodEndDate.diff(periodStartDate, 'days');
    const midDate = periodStartDate.clone().add(periodDuration/2, 'days');
    const dateUTC = midDate.valueOf();
    
    return {
      ...cutline,
      periodId: cutline.periodId,
      date: dateUTC,
      value: valuesMap[cutline.value],
    };
  });

  // Find the period for the latest activity
  let latestActivityPeriod = null;
  if (latestActivity) {
    const latestActivityDate = moment(latestActivity.date);
    latestActivityPeriod = assessmentCalendar.periods.find(period => {
      const periodStart = moment(period.startDate);
      const periodEnd = moment(period.endDate);
      return latestActivityDate.isBetween(periodStart, periodEnd, null, '[]');
    });
    if (latestActivityDate.isAfter(assessmentCalendar.periods[assessmentCalendar.periods.length - 1].endDate)) { 
      // if the latest activity date is after the last period, use the last period
      latestActivityPeriod = assessmentCalendar.periods[assessmentCalendar.periods.length - 1];
    }
  }

  // Find the cutline for the latest activity
  let latestActivityCutline = null;
  if (latestActivity && latestActivityPeriod) {
    // Get cutlines for the current period
    const periodCutlines = processedCutlines.filter(cutline => 
      cutline.periodId === latestActivityPeriod.periodIdentifier
    );
    
    const sortedCutlines = periodCutlines.sort((a, b) => a.percentile - b.percentile);
    
    // Find the cutline where the latest activity sore falls into by subtracting the cutline values from the score
    let score = latestActivity.score;    
    for (const cutline of sortedCutlines) {
      score -= cutline.value;
      if (score <= 0) {
        latestActivityCutline = cutline;
        break;
      }
    }
    !latestActivityCutline && (latestActivityCutline = sortedCutlines[sortedCutlines.length - 1]);
  }

  const cutlineSeriesMap = {};
  processedCutlines.forEach((cutline) => {
    if (!cutlineSeriesMap[cutline.cutoffLabel]) {
      cutlineSeriesMap[cutline.cutoffLabel] = {
        name: cutline.cutoffLabel,
        type: "area",
        data: [],
        color: cutline.hexCode,
        enableMouseTracking: false,
      }
    }
    cutlineSeriesMap[cutline.cutoffLabel].data.push([cutline.date, cutline.value]);
  });
  
  // Get unique cutline labels from scaledCutlines and reverse to get the correct render order
  const cutlineLabels = [...new Set(scaledCutlines.map(cutline => cutline.cutoffLabel))].reverse();
  
  cutlineSeries = [];
  cutlineLabels.forEach((label) => {
    if (cutlineSeriesMap[label]) {
      cutlineSeries.push(cutlineSeriesMap[label]);
    }
  });

  const highCutline = cutlineSeries && cutlineSeries[0]?.data;
  const lowCutline = cutlineSeries && cutlineSeries[cutlineSeries.length - 1]?.data;
  const highestCutpoint = scaledCutlines[numOfCutlines - 1]?.value;
  const highCutlineDiff = highestCutpoint - highCutline[0][1];

  // Determine the highest and lowest points on the graph
  const { yaxisMax } = determineHighPoints(data, highCutline, highestCutpoint);
  const { yaxisMin } = determineLowPoints(data, lowCutline, scaleId);

  const poorCutoff = lowCutline;
  const greatCutoff = highCutline.map((cutpoint) => [cutpoint[0], Math.max(yaxisMax, highestCutpoint) - highCutlineDiff]);
  cutlineSeries[0].data = greatCutoff;

  // handle legacy "On Track" cutline (25 to 75)
  const onTrackCutoff = highCutline.map((cutpoint, i) => [cutpoint[0], cutpoint[1] - lowCutline[i][1]]);

  const finalReport = {
    data,
    latestScore,
    latestPRScore,
    cutlineSeries,
    poorCutoff,
    greatCutoff,
    onTrackCutoff,
    yearStart: moment(yearStartDate).valueOf(),
    yearEnd: moment(yearEndDate).valueOf(),
    yMax: yaxisMax,
    yMin: yaxisMin,
    label: scaleId,
    scale: scaleId,
    trendline: [], // trendline populated async after report loads
    latestActivityCutline: latestActivityCutline || null
  };
  return finalReport;
}

function determineHighPoints(data, highCutline, highestCutpoint) {
  const highest = data.reduce( findHighScore, {score: -1000} );
  const highestScore = highest.score;
  let yaxisMax = highestScore * 1.1;
  if(highestCutpoint > highestScore) {
    yaxisMax = highestCutpoint * 1.1;
  }
  return {highestScore, highestCutpoint, yaxisMax};
}

function determineLowPoints(data, lowCutline, scaleId) {
  const lowestScore = data.reduce( findLowScore, {score: 10000} ).score;
  const lowestCutpoint = lowCutline.reduce( findLowScoreInTuple, [null,10000])[1];
  let yaxisMin = lowestScore  * 1.1;
  if(lowestCutpoint < lowestScore) {
    yaxisMin = lowestCutpoint;
  }
  if(yaxisMin > 0) {
    if(scaleId == 'AREA') { // TODO use enum
      if(yaxisMin > 4) {
        yaxisMin = 4;
      } else {
        yaxisMin = yaxisMin - 1;
      }
    } else {
      yaxisMin = 0;
    }
  } else {
    yaxisMin = yaxisMin * 1.1;
  }
  return {lowestScore, lowestCutpoint, yaxisMin};
}

function findHighScore( max, cur ) {
  if(cur.score > max.score) {
    return cur;
  }
  return max;
}

function findLowScore( min, cur ) {
  if(cur.score < min.score) {
    return cur;
  }
  return min;
}

function findHighScoreInTuple( max, cur ) {
  if(cur[1] > max[1]) {
    return cur;
  }
  return max;
}

function findLowScoreInTuple( min, cur ) {
  if(cur[1] < min[1]) {
    return cur;
  }
  return min;
}